import type React from 'react';
import { type FC, useEffect, useState } from 'react';

import { useStore } from '@nanostores/react';
import { useParams } from '@tanstack/react-router';
import classNames from 'classnames';

import {
  OBJECT_TYPE_ASSETS,
  type ObjectType,
} from '@iconik-io/iconik-api/src/types/object_types';
import { getProxyMediaType } from '@iconik-io/shared-lib/src/utils/get_proxy_media_type';
import { MEDIA_TYPE_VIDEO } from '@iconik-io/shared-lib/src/utils/media_type';
import { Icon } from '@iconik-io/uad';
import { useBasePlayerContext } from '@iconik-io/unified-player-ui-react';

import { Link } from '../../components/link/link';
import NoContentPanel from '../../components/no_content_panel/no_content_panel';
import SelectableItems from '../../components/selectable_items/selectable_items';
import { POSTERS } from '../../constants/help_urls';
import { segmentTrackEvent } from '../../ducks/analytics_tracking/thunks';
import {
  getEntityKeyframes,
  updateKeyframeAndPosterFromFile,
} from '../../ducks/entity_keyframes/thunks';
import { selectAssetProxy } from '../../ducks/pages/asset/selectors';
import { selectIsLatestVersion } from '../../ducks/version/selectors';
import { useAppDispatch, useAppSelector } from '../../store/react_redux_typed';
import Button from '../../ui/button/button';
import FileButton from '../../ui/forms/fields/file_button/file_button';
import GearBoxActions from '../../ui/gearbox_actions/gearbox_actions';
import LoadingBar from '../../ui/loading_bar/loading_bar';
import PostersContextMenu from './context_menu';
import PosterTypeContextMenu from './poster_type_context_menu';
import { getCanUploadPosters, selectPosters } from './selectors';

import styles from './entity_posters.module.css';

type Props = { entity?: ObjectType };

const PostersPanel: FC<Props> = ({ entity = OBJECT_TYPE_ASSETS }) => {
  const { store } = useBasePlayerContext();
  const currentTime = useStore(store.playableMedia.$currentTime);
  const compareActive = useStore(store.compare.$compareActive);

  const dispatch = useAppDispatch();
  const { id: entityId, versionId } = useParams({ strict: false });
  const object = useAppSelector((state) =>
    entityId
      ? (state.entities.assets[entityId] ??
        state.entities.collections[entityId] ??
        state.entities.search_items[entityId])
      : undefined,
  );

  const loading = useAppSelector((state) =>
    entityId ? state.entityKeyframes.loadingByEntityId[entityId] : false,
  );

  const assetProxy = useAppSelector((state) =>
    selectAssetProxy(state, entityId, versionId),
  );
  const isVideo =
    getProxyMediaType(assetProxy) === MEDIA_TYPE_VIDEO &&
    entity === OBJECT_TYPE_ASSETS;
  const isLatestVersion = useAppSelector((state) =>
    entity === OBJECT_TYPE_ASSETS
      ? selectIsLatestVersion(state, entityId, versionId)
      : true,
  );
  const posters = useAppSelector((state) => selectPosters(state, entityId));
  const canUploadPosters = useAppSelector((state) =>
    getCanUploadPosters(state, entityId, entity),
  );

  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  useEffect(() => {
    if (!entityId) {
      return;
    }
    dispatch(getEntityKeyframes(entity, entityId));
  }, [entity, entityId]);

  const onChooseFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(
      segmentTrackEvent({
        eventName: 'Create Custom Keyframe',
        properties: {
          Using: 'Custom image',
        },
      }),
    );
    if (event.target.files?.length && entityId) {
      const file = event.target.files[0];
      dispatch(updateKeyframeAndPosterFromFile({ entity, entityId, file }));
    }
  };

  if (!object) {
    return null;
  }
  const buttons = isLatestVersion ? (
    <div className={classNames({ [styles.hidden]: loading })}>
      <FileButton
        className={styles.file_button}
        onChange={onChooseFile}
        message="Upload new"
        buttonProps={{
          className: styles.action_element,
        }}
        multiple={false}
        accept="image/*"
      />
      {isVideo && currentTime !== undefined ? (
        <GearBoxActions
          triggerElement={
            <Button noBorder className={styles.action_element}>
              Take snapshot
            </Button>
          }
          contextItems={[object]}
          showIcons={false}
          contextMenu={{
            component: PosterTypeContextMenu,
            options: {
              currentTimeMs: currentTime * 1000,
              compareActive,
            },
          }}
        />
      ) : null}
    </div>
  ) : (
    <small>
      Go to the &nbsp;
      <Link to={`/asset/${entityId}`}>latest version</Link>
      &nbsp; to upload a new one
    </small>
  );
  if (!posters.length && !loading) {
    return (
      <NoContentPanel
        title={
          entity === 'collections'
            ? 'Set a cover image to represent the collection'
            : 'Set a cover image or video frame to represent the asset'
        }
        linkText="Learn more"
        linkHref={POSTERS}
        actionElement={buttons}
      />
    );
  }
  return (
    <div className={styles.root}>
      <LoadingBar
        className={classNames(styles.loading, {
          [styles.visible]: loading,
        })}
      />
      <SelectableItems
        contextMenu={PostersContextMenu}
        onSelectIds={setSelectedIds}
        items={posters}
        multiselect={false}
        aria={{ label: 'Posters' }}
      >
        <div className={styles.grid} role="none">
          {posters.map((poster, index) => (
            <div
              key={poster.id}
              data-id={poster.id}
              role="option"
              tabIndex={-1}
              aria-label={`Poster ${index + 1}`}
              aria-selected={selectedIds.includes(poster.id)}
              aria-current={
                poster.id === object.custom_poster ? 'true' : undefined
              }
              className={classNames(styles.grid_item, {
                [styles.selected]: selectedIds.includes(poster.id),
              })}
            >
              <img className={styles.poster_image} src={poster.url} alt="" />
              {poster.id === object.custom_poster ? (
                <div className={styles.current_custom_keyframe}>
                  <Icon name="snapshot" aria-hidden />
                </div>
              ) : null}
            </div>
          ))}
        </div>
      </SelectableItems>
      <div className={styles.sticky_buttons}>
        {canUploadPosters ? buttons : undefined}
      </div>
    </div>
  );
};

export default PostersPanel;
